import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { listDeposits } from '../../services';
import DataTable from '../../components/DataTable';
import UserSearchSelect from '../../components/UserSearchSelect';
import ApproveDepositDialog from './ApproveDepositDialog';

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  { value: 0, label: 'Pending' },
  { value: 1, label: 'Success' },
  { value: 2, label: 'Failed' },
];
const STATUS_LABELS = { 0: 'Pending', 1: 'Success', 2: 'Failed' };

function getErrorMessage(error) {
  return error.response?.data?.error || error.response?.data?.message || error.message || 'Request failed';
}

export default function DepositsTab() {
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [status, setStatus] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [approvingDeposit, setApprovingDeposit] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    listDeposits({
      search,
      userIds: selectedUsers.map((u) => u.id),
      status: status === '' ? undefined : status,
      sortBy,
      sortDir,
      page,
      pageSize: PAGE_SIZE,
    })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((error) => {
        if (!cancelled) toast.error(getErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [search, selectedUsers, status, sortBy, sortDir, page, reloadToken]);

  const columns = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'username', label: 'User' },
    { key: 'method_callback', label: 'Phương thức' },
    {
      key: 'trans_amount',
      label: 'Số tiền gốc',
      sortable: true,
      render: (row) => `${row.trans_amount?.toLocaleString('vi-VN')} ${row.trans_currency}`,
    },
    {
      key: 'received_amount',
      label: 'Quy đổi (USD)',
      sortable: true,
      render: (row) => row.received_amount?.toLocaleString('vi-VN', { minimumFractionDigits: 2 }),
    },
    {
      key: 'status',
      label: 'Trạng thái',
      sortable: true,
      render: (row) => STATUS_LABELS[row.status] ?? row.status,
    },
    {
      key: 'created_at',
      label: 'Thời gian',
      sortable: true,
      render: (row) => new Date(row.created_at).toLocaleString('vi-VN'),
    },
    {
      key: 'actions',
      label: 'Hành động',
      render: (row) =>
        row.status === 0 ? (
          <button
            type="button"
            onClick={() => setApprovingDeposit(row)}
            className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-medium text-white"
          >
            Duyệt
          </button>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-cyan-100 bg-white p-3">
        <div className="grid gap-3 md:grid-cols-3">
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Tìm theo trans_id/sender_name"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-cyan-400"
          />
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value === '' ? '' : Number(event.target.value));
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-cyan-400"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <UserSearchSelect
            value={selectedUsers}
            onChange={(users) => {
              setSelectedUsers(users);
              setPage(1);
            }}
            multiple
            placeholder="Lọc theo user..."
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={(nextSortBy, nextSortDir) => {
          setSortBy(nextSortBy);
          setSortDir(nextSortDir);
        }}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      {approvingDeposit && (
        <ApproveDepositDialog
          deposit={approvingDeposit}
          onClose={() => setApprovingDeposit(null)}
          onApproved={() => {
            setApprovingDeposit(null);
            setReloadToken((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}
